import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Check, ZoomIn, Ruler } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import ImageLightbox from "@/components/ImageLightbox";
import SizeGuideModal from "@/components/SizeGuideModal";
import CompleteTheLook from "@/components/CompleteTheLook";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [unit, setUnit] = useState("in");
  const [notifySize, setNotifySize] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lowNotice, setLowNotice] = useState(true);

  useEffect(() => {
    api.get("/site/status").then((r) => setLowNotice(r.data.low_stock_notice !== false)).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data); setColor(r.data.colors[0]?.name);
      setSize(r.data.sizes.length === 1 ? r.data.sizes[0] : null); setActive(0);
      api.get(`/products?collection=${r.data.collection || 'all'}`).then((rr) =>
        setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 4))).catch(() => {});
    }).catch(() => {});
  }, [id]);

  if (!product) return <div className="bg-ink min-h-screen pt-40 text-center font-mono text-graphite">Loading…</div>;

  const add = () => {
    if (!size) { toast.error("Please select a size."); return; }
    if (isOut(size)) { toast.error("This size is out of stock."); return; }
    addItem(product, size, color);
    toast.success(`${product.name} added to bag.`);
  };

  const soldOut = product.sold_out_sizes || [];
  const stockQty = (c, s) => {
    const v = product.stock?.[c]?.[s];
    return v === undefined || v === null ? Infinity : v;
  };
  const isOut = (s, c = color) => soldOut.includes(s) || stockQty(c, s) === 0;
  const allOut = (product.sizes || []).length > 0 && product.sizes.every((s) => isOut(s));
  const gallery = [
    ...((product?.images_by_color?.[color]) || product?.images || []),
    ...(product?.lifestyle_images || []),
  ];

  const pickSize = (s) => {
    if (isOut(s)) {
      setNotifySize(s); setNotifySent(false);
      if (!notifyEmail && user) setNotifyEmail(user.email);
      return;
    }
    setNotifySize(null);
    setSize(s);
  };

  const submitNotify = async () => {
    if (!notifyEmail.includes("@")) { toast.error("Enter a valid email."); return; }
    try {
      await api.post("/stock/notify", { email: notifyEmail, product_id: product.id, size: notifySize });
      setNotifySent(true);
    } catch {
      toast.error("Could not save your alert. Please try again.");
    }
  };

  return (
    <div className="bg-ink min-h-screen pt-28 pb-28" data-testid="product-page">
      <div className="container-sync">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-graphite hover:text-pulse mb-10">
          <ArrowLeft size={14} /> Back to shop
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          {/* gallery */}
          <div className="flex flex-col-reverse sm:flex-row gap-4">
            <div className="flex sm:flex-col gap-3">
              {gallery.map((img, i) => (
                <button key={img} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`w-16 h-16 overflow-hidden border bg-white ${active === i ? "border-pulse" : "border-border"}`}>
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
            <motion.div key={`${color}-${active}`} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="relative flex-1 aspect-square overflow-hidden bg-white group/zoom">
              <img src={gallery[active]} alt={product.name} className="w-full h-full object-contain cursor-zoom-in"
                onClick={() => setZoomOpen(true)} />
              <button onClick={() => setZoomOpen(true)} data-testid="pdp-zoom-btn" aria-label="Zoom image"
                className="absolute bottom-3 right-3 w-11 h-11 bg-ink/85 text-chalk hover:bg-ink flex items-center justify-center transition-colors">
                <ZoomIn size={18} />
              </button>
            </motion.div>
          </div>
          {zoomOpen && <ImageLightbox src={gallery[active]} alt={product.name} onClose={() => setZoomOpen(false)} />}
          {sizeGuideOpen && <SizeGuideModal product={product} onClose={() => setSizeGuideOpen(false)} />}

          {/* info */}
          <div className="lg:pl-6">
            <div className="flex items-center gap-3 mb-4">
              <p className="eyebrow !mb-0">{product.product_type || (product.collection === "pulse" ? "PULSE — Performance" : product.collection === "core" ? "CORE — Casual" : "Accessories")}</p>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-border text-chalk/70 px-2 py-0.5 shrink-0"
                data-testid="product-gender-label">
                {{ men: "Men's", women: "Women's" }[product.gender] || "Unisex"}
              </span>
            </div>
            <h1 className="font-anton uppercase text-4xl sm:text-6xl leading-[0.9]">{product.name}</h1>
            <div className="flex items-center gap-3 mt-4">
              <p className="font-mono text-xl">${product.price.toFixed(2)}</p>
              {product.final_sale && (
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-pulse/60 text-pulse px-2 py-1" data-testid="final-sale-badge">Final Sale</span>
              )}
            </div>
            <p className="text-chalk/90 text-lg leading-[1.85] mt-6 max-w-md whitespace-pre-line">{product.description}</p>

            {product.production_days > 0 && (
              <p className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-signal border border-signal/40 px-3 py-2" data-testid="production-badge">
                <span className="w-1.5 h-1.5 bg-signal rounded-full" />
                Made to order — production {product.production_min_days ? `${product.production_min_days}–${product.production_days}` : `${product.production_days}+`} business days
              </p>
            )}

            {/* colors */}
            <div className="mt-8">
              <span className="eyebrow">Color — {color}</span>
              <div className="flex gap-3 mt-3">
                {product.colors.map((c) => (
                  <button key={c.name} onClick={() => { setColor(c.name); setActive(0); if (size && isOut(size, c.name)) setSize(null); }} data-testid={`color-${c.name}`}
                    style={{ backgroundColor: c.hex }}
                    className={`w-9 h-9 rounded-full border-2 transition-transform ${color === c.name ? "border-pulse scale-110" : "border-border"}`}
                    aria-label={c.name} />
                ))}
              </div>
              {product.skus?.[color] && (
                <p className="font-mono text-[11px] uppercase tracking-wider text-graphite mt-3" data-testid="variant-sku">SKU — {product.skus[color]}{size ? `-${size}` : ""}</p>
              )}
            </div>

            {/* sizes */}
            <div className="mt-8">
              <div className="flex justify-between items-center">
                <span className="eyebrow">Select size</span>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[11px] text-graphite uppercase">{product.fit}</span>
                  {product.size_guide?.rows?.length > 0 && (
                    <button onClick={() => setSizeGuideOpen(true)} data-testid="size-guide-btn"
                      className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-signal hover:text-pulse underline underline-offset-4 transition-colors">
                      <Ruler size={13} /> Size Guide
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {product.sizes.map((s) => {
                  const out = isOut(s);
                  return (
                    <button key={s} onClick={() => pickSize(s)} data-testid={`size-${s}`}
                      className={`font-mono text-xs min-w-[52px] h-12 border transition-colors ${
                        out ? "border-pulse/50 text-chalk/40 line-through decoration-pulse decoration-2"
                          : size === s ? "bg-chalk text-ink border-chalk" : "border-border text-chalk/80 hover:border-chalk"
                      }`}>{s}</button>
                  );
                })}
              </div>
              {allOut && (
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-destructive" data-testid="out-of-stock-status">
                  Out of stock in {color}
                </p>
              )}
              {lowNotice && size && Number.isFinite(stockQty(color, size)) && stockQty(color, size) > 0 && stockQty(color, size) <= 5 && (
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400" data-testid="low-stock-note">
                  Only {stockQty(color, size)} left in {color} / {size}
                </p>
              )}
              {notifySize && (
                <div className="mt-4 border border-border p-4" data-testid="notify-me-box">
                  {notifySent ? (
                    <p className="font-mono text-[11px] uppercase tracking-wider text-signal" data-testid="notify-success">
                      You're on the list — we'll email you when {notifySize} is back.
                    </p>
                  ) : (
                    <>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-graphite">
                        Size {notifySize} is sold out — get notified when it's back:
                      </p>
                      <div className="flex gap-3 mt-3">
                        <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                          placeholder="you@email.com" data-testid="notify-email"
                          className="flex-1 bg-transparent border-b border-border py-2 font-mono text-sm focus:border-pulse outline-none" />
                        <button onClick={submitNotify} data-testid="notify-submit"
                          className="font-mono text-[11px] uppercase tracking-wider border border-chalk px-4 hover:bg-chalk hover:text-ink transition-colors">
                          Notify Me
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <button onClick={add} data-testid="add-to-cart" className="btn-pulse w-full mt-9">
              Add to Bag — ${product.price.toFixed(2)}
            </button>

            <Accordion type="single" collapsible className="mt-10 border-t border-border">
              <AccordionItem value="fabric" className="border-border">
                <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.15em] hover:no-underline">Fabric & Care</AccordionTrigger>
                <AccordionContent className="text-graphite text-[15px] leading-[1.85]">{product.fabric}</AccordionContent>
              </AccordionItem>
              {product.specs && (
                <AccordionItem value="specs" className="border-border">
                  <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.15em] hover:no-underline">Specifications</AccordionTrigger>
                  <AccordionContent>
                    <dl className="space-y-2" data-testid="specs-list">
                      {Object.entries(product.specs).map(([k, v]) => (
                        <div key={k} className="flex justify-between font-mono text-[11px] uppercase tracking-wider border-b border-border/50 pb-2">
                          <dt className="text-graphite">{k}</dt>
                          <dd className="text-chalk">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </AccordionContent>
                </AccordionItem>
              )}
              <AccordionItem value="fit" className="border-border">
                <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.15em] hover:no-underline">Fit & Size Guide</AccordionTrigger>
                <AccordionContent className="text-graphite text-[15px] leading-[1.85]">
                  <p>{product.fit} Model is 6'1" wearing size M. Between sizes? Size up for a relaxed feel.</p>
                  {product.size_guide?.rows?.length > 0 && (
                    <div className="mt-4">
                      <div className="flex gap-1 mb-3">
                        {["in", "cm"].map((u) => (
                          <button key={u} onClick={() => setUnit(u)} data-testid={`unit-toggle-${u}`}
                            className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1 border transition-colors ${
                              unit === u ? "bg-chalk text-ink border-chalk" : "border-border text-graphite hover:border-chalk"
                            }`}>{u === "in" ? "Inches" : "CM"}</button>
                        ))}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full font-mono text-[11px]" data-testid="size-guide-table">
                          <thead>
                            <tr className="text-left uppercase tracking-wider text-chalk/70 border-b border-border">
                              <th className="py-2 pr-3 font-normal">Size</th>
                              {product.size_guide.columns.map((c) => (
                                <th key={c} className="py-2 pr-3 font-normal">{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {product.size_guide.rows.map((r) => (
                              <tr key={r.size} className="border-b border-border/50">
                                <td className="py-2 pr-3 text-chalk">{r.size}</td>
                                {r.values.map((v, i) => (
                                  <td key={product.size_guide.columns[i]} className="py-2 pr-3">{unit === "in" ? `${v[0]}"` : `${v[1]} cm`}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-graphite text-[10px] font-mono mt-3">1–3 cm sizing differences may occur.</p>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ship" className="border-border">
                <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.15em] hover:no-underline">Shipping & Returns</AccordionTrigger>
                <AccordionContent className="text-graphite text-[15px] leading-[1.85] space-y-2">
                  {product.final_sale && (
                    <p className="text-pulse" data-testid="final-sale-note">This item is Final Sale — not eligible for return or refund, except where required by law or if the item is defective or incorrectly fulfilled.</p>
                  )}
                  <p><span className="text-chalk/80">Production: 3–7 business days.</span> Every SYNC piece is made to order.</p>
                  <p><span className="text-chalk/80">Shipping: 7–10 business days after production.</span> Standard $5.99 — free on US orders over $100. Express available at checkout.</p>
                  <p>You'll get a carrier + tracking number by email the moment your order ships. Full details: <Link to="/shipping" className="text-signal hover:underline">Shipping Policy</Link> · <Link to="/returns" className="text-signal hover:underline">Returns & Exchanges</Link>.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="flex gap-6 mt-8 text-graphite">
              {["Made to move", "Easy care", "Signature fit"].map((f) => (
                <span key={f} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider">
                  <Check size={13} className="text-signal" /> {f}
                </span>
              ))}
            </div>
          </div>
        </div>

        <CompleteTheLook productId={product.id} color={color} />

        {related.length > 0 && (
          <div className="mt-28">
            <p className="eyebrow mb-10">You might also like</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
